<template>
  <page>
    <sidebar slot="sidebar" />
    <span slot="header">Components</span>
    <span slot="breadcrumbs">
      <b-breadcrumb :items="breadcrumbs" />
    </span>
    <form-wrapper>
      <b-form @submit="onSubmit">
        <b-form-group>
          <b-form-checkbox
            :disabled="saving"
            v-model="is_active"
            value="1"
            unchecked-value="0"
            @input="setActive"
          >Active</b-form-checkbox>
        </b-form-group>

        <b-form-group>
          <label for="name-input">Name</label>
          <b-form-input
            v-validate="'required'"
            id="name-input"
            :disabled="saving"
            :class="{ 'is-invalid' : errors.has('name') || apiErrors.name.length }"
            :value="fields.name === null ? component.name : fields.name"
            class="form-control"
            type="text"
            name="name"
            @input="setName"
          />
          <validation-errors :validation-errors="[errors.first('name')]" />
          <validation-errors v-if="!errors.has('name')" :validation-errors="apiErrors.name" />
        </b-form-group>

        <b-form-group>
          <label for="description-input">Description</label>
          <b-form-textarea
            v-validate="'required'"
            id="description-input"
            :disabled="saving"
            :class="{ 'is-invalid' : errors.has('description') || apiErrors.description.length }"
            :rows="3"
            :max-rows="6"
            :value="fields.description === null ? component.description : fields.description"
            type="text"
            name="description"
            @input="setDescription"
          />
          <validation-errors :validation-errors="[errors.first('description')]" />
          <validation-errors
            v-if="!errors.has('description')"
            :validation-errors="apiErrors.description"
          />
        </b-form-group>

        <b-form-group v-if="showCurrentImage">
          <b-row>
            <b-col cols="12" lg="7">
              <label for="preview">Current Image</label>
              <image-preview :src="currentImageSrc" @click.native="displayImage(true)" />
            </b-col>
            <b-col cols="12" lg="5" class="mt-lg-4 mt-2">
              <span class="name">Name:</span>
              {{ currentImage.name }}
              <br />
              <span class="name">Size:</span>
              {{ readableFilesize(currentImage.size) }}
              <br />
              <span class="name">Created:</span>
              {{ readableDate(currentImage.created) }}
            </b-col>
          </b-row>
        </b-form-group>

        <b-form-group>
          <b-form-select
            v-validate="'included:upload,library'"
            :options="imageOptions"
            :disabled="saving"
            :class="{ 'is-invalid' : errors.has('preference') }"
            v-model="imageSelection"
            name="preference"
            @change="imageSelect"
          />
          <validation-errors :validation-errors="[errors.first('preference')]" />
        </b-form-group>

        <span v-if="imageSelection === 'upload'">
          <b-form-group>
            <image-upload
              @fileForEdit="setImage"
              @cropImage="setCropImage"
              @name="setImageName"
              @imageUploadHasError="setImageUploadHasError"
            />
          </b-form-group>

          <b-form-group>
            <label for="image-name-input">Image Name</label>
            <b-form-input
              v-validate="'required'"
              id="image-name-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('imageName') || apiErrors.imageName.length }"
              :value="fields.imageName"
              class="form-control"
              type="text"
              data-vv-as="image name"
              name="imageName"
              @input="setImageName"
            />
            <validation-errors :validation-errors="[errors.first('imageName')]" />
            <validation-errors
              v-if="!errors.has('imageName')"
              :validation-errors="apiErrors.imageName"
            />
          </b-form-group>
        </span>

        <b-form-group v-if="imageSelection === 'library'">
          <b-button
            :disabled="saving"
            variant="outline-secondary"
            @click="showMediaLibrary = true"
          >Browse Library</b-button>
        </b-form-group>

        <b-form-group v-if="selectedImage && imageSelection === 'library'">
          <b-row>
            <b-col cols="12" lg="7">
              <label for="preview">New Image</label>
              <image-preview :src="selectedImageSrc" @click.native="displayImage(true)" />
            </b-col>
            <b-col cols="12" lg="5" class="mt-lg-4 mt-2">
              <span class="name">Name:</span>
              {{ selectedImage.name }}
              <br />
              <span class="name">Size:</span>
              {{ readableFilesize(selectedImage.size) }}
              <br />
              <span class="name">Created:</span>
              {{ readableDate(selectedImage.created) }}
            </b-col>
          </b-row>
        </b-form-group>

        <b-form-group class="form-buttons">
          <b-button
            :disabled="saving"
            variant="secondary"
            @click="$router.push({name: 'admin-components'})"
          >Cancel</b-button>
          <b-button :disabled="disabled" type="submit" variant="primary">
            Confirm
            <span v-if="saving">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
          </b-button>
        </b-form-group>

        <b-modal v-model="showMediaLibrary" title="Media Library" centered hide-footer size="lg">
          <media-table
            v-if="showMediaLibrary"
            :view="mediaView"
            :show-type-select="false"
            :show-module-select="false"
            context="selectOne"
            type="image"
            @selectMedia="setImageID"
          />
        </b-modal>

        <b-modal
          v-model="showMediaModal"
          :title="component.main_image.name"
          centered
          hide-footer
          size="lg"
        >
          <b-img :src="component.main_image.url" />
        </b-modal>

        <form-success :success="success" class="form-alert">Component Updated!</form-success>
      </b-form>
    </form-wrapper>
  </page>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import {
  capitalizeFirstLetter,
  readableDate,
  readableFilesize
} from '../../../../helpers'
import formSuccess from '../../../common/form/form-success'
import formWrapper from '../../../common/form/form-wrapper'
import imagePreview from '../../../common/image-preview'
import imageUpload from '../../../common/form/image-upload'
import mediaTable from '../media/table'
import page from '../../../templates/page'
import sidebar from '../../../sidebar'
import validationErrors from '../../../common/form/validation-errors'
import moment from 'moment'

export default {
  components: {
    formSuccess,
    formWrapper,
    imagePreview,
    imageUpload,
    mediaTable,
    page,
    sidebar,
    validationErrors
  },
  data() {
    return {
      imageSelection: null,
      imageOptions: [
        { text: 'Update Component Image', value: null },
        { text: 'Upload a new image', value: 'upload' },
        { text: 'Choose an image from the library', value: 'library' }
      ],
      showMediaLibrary: false,
      selectedImage: null,
      breadcrumbs: [
        {
          text: 'Modules',
          to: { name: 'admin-modules' }
        },
        {
          text: 'Components',
          to: { name: 'admin-components' }
        },
        {
          text: 'Edit',
          active: true
        }
      ],
      imageUploadHasError: false,
      cropImage: false,
      showMediaModal: false
    }
  },
  computed: {
    ...mapState('components', {
      component: 'current'
    }),
    ...mapState('components/forms/update', [
      'apiErrors',
      'fields',
      'success',
      'saving'
    ]),
    ...mapState('config', {
      apiConfig: 'data'
    }),
    ...mapState('media', {
      mediaView: '_defaultView'
    }),
    is_active: {
      get() {
        return this.component.is_active
      },
      set(newValue) {}
    },
    disabled() {
      return this.errors.any() || this.saving || this.imageUploadHasError
    },
    showCurrentImage() {
      return this.component.main_image && this.component.main_image.id
    },
    currentImageSrc() {
      if (this.component.main_image.thumbnail) {
        return this.component.main_image.thumbnail.url
      }
      return this.component.main_image.url
    },
    currentImage () {
      // eslint-disable-next-line
      let image = this.component.main_image
      image.created = moment(image.created_at.date).unix()
      return image
    },
    selectedImageSrc() {
      if (this.selectedImage.thumbnail) {
        return this.selectedImage.thumbnail.url
      }
      return this.selectedImage.url
    }
  },
  watch: {
    success(newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.resetFields()
      }
    }
  },
  created() {
    this.$store.commit('components/forms/update/resetState')
  },
  methods: {
    capitalizeFirstLetter,
    readableFilesize,
    readableDate,
    ...mapActions({
      updateComponent: 'components/update'
    }),
    onSubmit(event) {
      event.preventDefault()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.updateComponent({
            componentID: this.component.id
          })
        }
      })
    },
    setActive(event) {
      this.$store.commit('components/forms/update/setField', {
        field: 'active',
        value: event
      })
    },
    setName(event) {
      this.$store.commit('components/forms/update/setField', {
        field: 'name',
        value: event
      })
    },
    setDescription(event) {
      this.$store.commit('components/forms/update/setField', {
        field: 'description',
        value: event
      })
    },
    setImage(image) {
      let imageName = null
      let first = true
      if (this.fields.image) {
        imageName = this.fields.image.name
        first = false
      }

      this.$store.commit('components/forms/update/setField', {
        field: 'image',
        value: image
      })

      const updatedImageName = this.fields.image.name
      const changed = imageName !== updatedImageName

      if (
        this.fields.imageName === '' ||
        this.fields.imageName === null ||
        (!first && changed)
      ) {
        this.$store.commit('components/forms/update/setField', {
          field: 'imageName',
          value: updatedImageName.replace(/\.[^/.]+$/, '')
        })
      }
    },
    setImageName(event) {
      this.$store.commit('components/forms/update/setField', {
        field: 'imageName',
        value: event
      })
    },
    setImageID(event) {
      this.selectedImage = event
      this.$store.commit('components/forms/update/setField', {
        field: 'imageID',
        value: event.id
      })
      this.showMediaLibrary = false
    },
    imageSelect(event) {
      if (event === 'library') {
        this.showMediaLibrary = true
        this.$store.commit('components/forms/update/setField', {
          field: 'image',
          value: null
        })
        this.$store.commit('components/forms/update/setField', {
          field: 'imageName',
          value: null
        })
      }

      if (event === 'upload') {
        this.selectedImage = null
        this.$store.commit('components/forms/update/setField', {
          field: 'imageID',
          value: null
        })
      }

      if (this.cropImage) {
        this.$store.commit('media/forms/store/setField', {
          field: 'crop_image',
          value: this.cropImage
        })
      }
    },
    reset(event) {
      this.errors.clear()
      this.imageSelection = null
      this.selectedImage = null
      this.imageUploadHasError = false
      this.cropImage = false
    },
    resetFields(event) {
      this.reset()
      this.$store.commit('components/forms/update/resetFields')
    },
    displayImage(show) {
      this.showMediaModal = show
    },
    setCropImage(value) {
      this.cropImage = value
    },
    setImageUploadHasError(hasError) {
      this.imageUploadHasError = hasError
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/admin/components/edit.vue